import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { X, Mail, Lock, User, Loader, Sparkles, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import { supabase } from '../supabaseClient';

const AuthModal = ({ onClose, initialMode = 'signup' }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode === 'login' ? 'login' : 'register');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    goal: ''
  });

  const goals = [
    { id: 'weight-loss', emoji: '🔥', label: 'Perdre du poids', desc: 'Atteindre mon poids idéal' },
    { id: 'muscle-building', emoji: '💪', label: 'Prendre du muscle', desc: 'Développer ma masse' },
    { id: 'health', emoji: '🌱', label: 'Manger plus sain', desc: 'Améliorer mon alimentation' },
    { id: 'energy', emoji: '⚡', label: 'Plus d\'énergie', desc: 'Me sentir en forme' }
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleGoalSelect = (goalId) => {
    setFormData({ ...formData, goal: goalId });
  };

  // Mot de passe oublié
  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError('Entrez votre email pour réinitialiser votre mot de passe');
      return;
    }
    setLoading(true);
    setError('');
    
    const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
      redirectTo: `${window.location.origin}/#/reset-password`,
    });
    
    if (error) {
      setError('Une erreur est survenue. Vérifiez votre email.');
    } else {
      setSuccess('Email envoyé ! Vérifiez votre boîte de réception.');
    }
    setLoading(false);
  };

  // Connexion par lien magique
  const handleMagicLink = async () => {
    if (!formData.email) {
      setError('Entrez votre email pour recevoir un lien de connexion');
      return;
    }
    setLoading(true);
    setError('');
    
    const { error } = await supabase.auth.signInWithOtp({
      email: formData.email,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
      }
    });
    
    if (error) {
      setError('Une erreur est survenue. Vérifiez votre email.');
    } else {
      setSuccess('Lien envoyé ! Vérifiez votre boîte de réception.');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (mode === 'login') {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        onClose();
      } else {
        setError(result.error === 'Invalid login credentials' 
          ? 'Email ou mot de passe incorrect' 
          : result.error);
      }
    } else {
      if (step === 1) {
        if (!formData.name || !formData.email || !formData.password) {
          setError('Veuillez remplir tous les champs');
          setLoading(false);
          return;
        }
        if (formData.password.length < 6) {
          setError('Mot de passe trop court (6 caractères minimum)');
          setLoading(false);
          return;
        }
        setStep(2);
        setLoading(false);
        return;
      }

      if (!formData.goal) {
        setError('Veuillez choisir votre objectif');
        setLoading(false);
        return;
      }

      const result = await register(formData);
      if (result.success) {
        onClose();
      } else {
        if (result.error.includes('already registered') || result.error.includes('already been registered')) {
          setError('Cet email est déjà utilisé');
        } else {
          setError(result.error);
        }
      }
    }
    setLoading(false);
  };

  const renderTitle = () => {
    if (mode === 'forgot') return 'Mot de passe oublié ?';
    if (mode === 'magic') return 'Connexion par email';
    if (mode === 'login') return 'Bon retour parmi nous !';
    return step === 1 ? 'Créez votre compte' : 'Votre objectif ?';
  };

  const renderSubtitle = () => {
    if (mode === 'forgot') return 'Entrez votre email pour recevoir un lien de réinitialisation';
    if (mode === 'magic') return 'Recevez un lien de connexion directement dans votre boîte mail';
    if (mode === 'login') return 'Connectez-vous pour retrouver votre historique';
    return step === 1 ? 'Inscription gratuite en 30 secondes' : 'Indiquez-nous ce qui vous motive';
  };

  const renderEmoji = () => {
    if (mode === 'forgot') return '🔐';
    if (mode === 'magic') return '✨';
    if (mode === 'login') return '👋';
    return step === 1 ? '🚀' : '🎯';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="modal-header">
          <div className="modal-emoji">{renderEmoji()}</div>
          <h2>{renderTitle()}</h2>
          <p className="modal-subtitle">{renderSubtitle()}</p>
        </div>

        {success ? (
          <div className="auth-success">
            <CheckCircle size={48} />
            <p>{success}</p>
            <button 
              className="auth-submit" 
              onClick={() => { setMode('login'); setSuccess(''); }}
            >
              Retour à la connexion
            </button>
          </div>
        ) : (
          <>
            {/* Mode Mot de passe oublié */}
            {mode === 'forgot' && (
              <div className="auth-form">
                <div className="input-group">
                  <Mail size={18} className="input-icon" />
                  <input
                    type="email"
                    name="email"
                    placeholder="Votre email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                  />
                </div>
                {error && <div className="auth-error">{error}</div>}
                <button 
                  type="button" 
                  className="auth-submit"
                  disabled={loading}
                  onClick={handleForgotPassword}
                >
                  {loading ? <Loader size={20} className="spin" /> : (
                    <>Envoyer le lien <ArrowRight size={18} /></>
                  )}
                </button>
                <button 
                  type="button" 
                  className="auth-back"
                  onClick={() => { setMode('login'); setError(''); }}
                >
                  <ArrowLeft size={16} /> Retour à la connexion
                </button>
              </div>
            )}

            {/* Mode Lien magique */}
            {mode === 'magic' && (
              <div className="auth-form">
                <div className="input-group">
                  <Mail size={18} className="input-icon" />
                  <input
                    type="email"
                    name="email"
                    placeholder="Votre email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                  />
                </div>
                {error && <div className="auth-error">{error}</div>}
                <button 
                  type="button" 
                  className="auth-submit"
                  disabled={loading}
                  onClick={handleMagicLink}
                >
                  {loading ? <Loader size={20} className="spin" /> : (
                    <>Recevoir le lien <ArrowRight size={18} /></>
                  )}
                </button>
                <button 
                  type="button" 
                  className="auth-back"
                  onClick={() => { setMode('login'); setError(''); }}
                >
                  <ArrowLeft size={16} /> Retour à la connexion
                </button>
              </div>
            )}

            {/* Mode Connexion / Inscription */}
            {(mode === 'login' || mode === 'register') && (
              <form onSubmit={handleSubmit} className="auth-form">
                {mode === 'register' && step === 2 ? (
                  <div className="goals-grid">
                    {goals.map(goal => (
                      <button
                        key={goal.id}
                        type="button"
                        className={`goal-card ${formData.goal === goal.id ? 'selected' : ''}`}
                        onClick={() => handleGoalSelect(goal.id)}
                      >
                        <span className="goal-emoji">{goal.emoji}</span>
                        <span className="goal-label">{goal.label}</span>
                        <span className="goal-desc">{goal.desc}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <>
                    {mode === 'register' && (
                      <div className="input-group">
                        <User size={18} className="input-icon" />
                        <input
                          type="text"
                          name="name"
                          placeholder="Votre prénom"
                          value={formData.name}
                          onChange={handleChange}
                          autoComplete="name"
                        />
                      </div>
                    )}

                    <div className="input-group">
                      <Mail size={18} className="input-icon" />
                      <input
                        type="email"
                        name="email"
                        placeholder="Votre email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                      />
                    </div>

                    <div className="input-group">
                      <Lock size={18} className="input-icon" />
                      <input
                        type="password"
                        name="password"
                        placeholder="Mot de passe"
                        value={formData.password}
                        onChange={handleChange}
                        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                      />
                    </div>
                  </>
                )}

                {error && <div className="auth-error">{error}</div>}

                <button 
                  type="submit" 
                  className="auth-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader size={20} className="spin" />
                  ) : (
                    <>
                      {mode === 'login' 
                        ? 'Se connecter' 
                        : step === 1 
                          ? 'Continuer' 
                          : 'Commencer'}
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                {/* Options connexion */}
                {mode === 'login' && (
                  <div className="auth-options">
                    <button type="button" onClick={() => { setMode('forgot'); setError(''); }}>
                      Mot de passe oublié ?
                    </button>
                    <span>•</span>
                    <button type="button" onClick={() => { setMode('magic'); setError(''); }}>
                      Connexion par lien
                    </button>
                  </div>
                )}
              </form>
            )}

            {(mode === 'login' || mode === 'register') && (
              <>
                <div className="auth-switch">
                  {mode === 'login' ? (
                    <p>
                      Pas encore inscrit ?{' '}
                      <button onClick={() => { setMode('register'); setStep(1); setError(''); }}>
                        Créer un compte
                      </button>
                    </p>
                  ) : (
                    <p>
                      Déjà un compte ?{' '}
                      <button onClick={() => { setMode('login'); setStep(1); setError(''); }}>
                        Se connecter
                      </button>
                    </p>
                  )}
                </div>

                <div className="auth-benefits">
                  <Sparkles size={16} />
                  <span>100% gratuit • Sans engagement • Données sécurisées</span>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
